/**
 * @module linkage/qbFields/useQBLinkagePipeline
 * @description 维/指/筛选变化时 L3 联动 — 唯一 QB→Settings patch 入口（与切图 L2 分离）
 *
 * 方案 B：useQBCategoryPartials 产出 categories + partials → 单 effect merge + pipeline
 *
 * ## 不订阅 analysisRunning
 *
 * QB 联动只应在 **QB 字段**（维/指/raw 列/筛选等）变化时运行。
 * Confirm → Search 重算会把 `analysisRunning` 设为 true，但 QB 列表未变；若 effect 依赖
 * `analysisRunning`，会在 Confirm 后误唤醒 pipeline，合并过期的 partial（如 stale metricRemoved）
 * 并 MERGE_QB_PATCH 覆盖刚 COMMIT_DRAFT 的条件格式。
 * effect 仅依赖 `qb.categories` / `qb.partials` / `dispatch`。
 */

import { useEffect, useRef } from 'react';
import { useChartSettingsDispatch, useChartSettingsSelector } from '../../context';
import { useQuerySelector } from '../../hooks/deps';
import { CSActionType } from '../../types';
import { COMPARE_STATES_DEBOUNCE } from '../../utils/constants';
import { mergeQBChangeSets } from './qbLinkage/detectChanges';
import { runQBLinkagePipeline } from './qbLinkage/runPipeline';
import { getQBLinkageHandlers } from './qbLinkage/registry';
import { useQBCategoryPartials } from './useQBCategoryPartials';

export function useQBLinkagePipeline(): void {
  const dispatch = useChartSettingsDispatch();
  const qb = useQBCategoryPartials();
  const chartType = useQuerySelector(s => s.chartType);

  const base = useChartSettingsSelector(s => s.base);
  const draft = useChartSettingsSelector(s => s.draft);

  const didBootstrapRef = useRef(false);
  const debounceTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const baseRef = useRef(base);
  const draftRef = useRef(draft);
  const chartTypeRef = useRef(chartType);
  baseRef.current = base;
  draftRef.current = draft;
  chartTypeRef.current = chartType;

  useEffect(() => {
    if (!didBootstrapRef.current) {
      qb.commitPrevSnapshot();
      didBootstrapRef.current = true;
      return;
    }

    // categories 为空表示各类 partial 均为 UNCHANGED（含 commit 后 prevVersion 重算结果）
    if (!qb.categories.size) {
      return;
    }

    if (debounceTimer.current) {
      clearTimeout(debounceTimer.current);
    }

    const { categories, partials, nextSnapshot } = qb;

    debounceTimer.current = setTimeout(() => {
      const changeSet = mergeQBChangeSets(
        categories,
        nextSnapshot,
        ...partials,
      );
      qb.commitPrevSnapshot();

      if (changeSet.events.size === 0) {
        return;
      }

      /*
       * 新建图 skip pipeline（方案 B）：当前不实施，详见
       * docs/qb-pipeline-skip-new-chart-plan.md
       * 现状：handler 级 guard 使空 base/draft 时 patch 通常为空。
       */
      const patch = runQBLinkagePipeline(
        changeSet,
        {
          base: baseRef.current,
          draft: draftRef.current,
          chartType: chartTypeRef.current,
          qb: {
            columnDimensions: nextSnapshot.columnDimensions,
            columnMetrics: nextSnapshot.columnMetrics,
            columnRawColumns: nextSnapshot.columnRawColumns,
            columnFilters: nextSnapshot.columnFilters,
            columnUserFilters: nextSnapshot.columnUserFilters,
            hasUserFilters: nextSnapshot.hasUserFilters,
            mode: nextSnapshot.mode,
          },
        },
        getQBLinkageHandlers(),
      );

      if (
        !Object.keys(patch.base).length &&
        !Object.keys(patch.draft).length
      ) {
        return;
      }

      dispatch({
        type: CSActionType.MERGE_QB_PATCH,
        payload: {
          base: Object.keys(patch.base).length ? patch.base : undefined,
          draft: Object.keys(patch.draft).length ? patch.draft : undefined,
        },
      });
    }, COMPARE_STATES_DEBOUNCE);

    return () => {
      if (debounceTimer.current) {
        clearTimeout(debounceTimer.current);
      }
    };
  }, [qb.categories, qb.partials, dispatch]);
}
